export interface LanguageOption {
  value: string;
  text: string;
}

const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { value: "ar-sa", text: "Arabic (ar-sa)" },
  { value: "bn-bd", text: "Bengali (bn-bd)" },
  { value: "bg-bg", text: "Bulgarian (bg-bg)" },
  { value: "ca-es", text: "Catalan (ca-es)" },
  { value: "zh-cn", text: "Chinese Simplified (zh-cn)" },
  { value: "zh-tw", text: "Chinese Traditional (zh-tw)" },
  { value: "hr-hr", text: "Croatian (hr-hr)" },
  { value: "cs-cz", text: "Czech (cs-cz)" },
  { value: "da-dk", text: "Danish (da-dk)" },
  { value: "nl-nl", text: "Dutch (nl-nl)" },
  { value: "nl-be", text: "Dutch (nl-be)" },
  { value: "en-us", text: "English (en-us)" },
  { value: "en-gb", text: "English (en-gb)" },
  { value: "en-au", text: "English (en-au)" },
  { value: "en-ca", text: "English (en-ca)" },
  { value: "et-ee", text: "Estonian (et-ee)" },
  { value: "fil-ph", text: "Filipino (fil-ph)" },
  { value: "fi-fi", text: "Finnish (fi-fi)" },
  { value: "fr-fr", text: "French (fr-fr)" },
  { value: "fr-ca", text: "French (fr-ca)" },
  { value: "fr-ch", text: "French (fr-ch)" },
  { value: "de-de", text: "German (de-de)" },
  { value: "de-at", text: "German (de-at)" },
  { value: "de-ch", text: "German (de-ch)" },
  { value: "el-gr", text: "Greek (el-gr)" },
  { value: "he-il", text: "Hebrew (he-il)" },
  { value: "hi-in", text: "Hindi (hi-in)" },
  { value: "hu-hu", text: "Hungarian (hu-hu)" },
  { value: "id-id", text: "Indonesian (id-id)" },
  { value: "it-it", text: "Italian (it-it)" },
  { value: "ja-jp", text: "Japanese (ja-jp)" },
  { value: "ko-kr", text: "Korean (ko-kr)" },
  { value: "lv-lv", text: "Latvian (lv-lv)" },
  { value: "lt-lt", text: "Lithuanian (lt-lt)" },
  { value: "ms-my", text: "Malay (ms-my)" },
  { value: "nb-no", text: "Norwegian Bokmål (nb-no)" },
  { value: "fa-ir", text: "Persian (fa-ir)" },
  { value: "pl-pl", text: "Polish (pl-pl)" },
  { value: "pt-br", text: "Portuguese (pt-br)" },
  { value: "pt-pt", text: "Portuguese (pt-pt)" },
  { value: "ro-ro", text: "Romanian (ro-ro)" },
  { value: "ru-ru", text: "Russian (ru-ru)" },
  { value: "sr-rs", text: "Serbian (sr-rs)" },
  { value: "sk-sk", text: "Slovak (sk-sk)" },
  { value: "sl-si", text: "Slovenian (sl-si)" },
  { value: "es-es", text: "Spanish (es-es)" },
  { value: "es-mx", text: "Spanish (es-mx)" },
  { value: "es-ar", text: "Spanish (es-ar)" },
  { value: "sw-ke", text: "Swahili (sw-ke)" },
  { value: "sv-se", text: "Swedish (sv-se)" },
  { value: "th-th", text: "Thai (th-th)" },
  { value: "tr-tr", text: "Turkish (tr-tr)" },
  { value: "uk-ua", text: "Ukrainian (uk-ua)" },
  { value: "ur-pk", text: "Urdu (ur-pk)" },
  { value: "vi-vn", text: "Vietnamese (vi-vn)" },
];

export function getSupportedLanguages(): LanguageOption[] {
  return SUPPORTED_LANGUAGES;
}

/**
 * Derives the country code from a language code for flag display.
 * e.g. "pt-br" → "br", "en-us" → "us", "en" → "gb" (fallback)
 */
export function getFlagCode(languageCode: string): string {
  const code = languageCode.toLowerCase();
  const parts = code.split("-");
  if (parts.length >= 2) return parts[1];
  // Single language codes: map common ones to country codes
  const map: Record<string, string> = {
    en: "gb",
    pt: "pt",
    es: "es",
    fr: "fr",
    de: "de",
    it: "it",
    nl: "nl",
    ru: "ru",
    zh: "cn",
    ja: "jp",
    ko: "kr",
    ar: "sa",
    hi: "in",
    tr: "tr",
    pl: "pl",
    sv: "se",
    da: "dk",
    fi: "fi",
    nb: "no",
    cs: "cz",
    sk: "sk",
    ro: "ro",
    hu: "hu",
    uk: "ua",
    el: "gr",
    he: "il",
    id: "id",
    th: "th",
    vi: "vn",
  };
  return map[code] || code;
}

/**
 * Returns a human-readable language name using the Intl API.
 * e.g. "pt-br" → "Portuguese (Brazil)", "en-us" → "English (United States)"
 */
export function getLanguageName(languageCode: string): string {
  try {
    const names = new Intl.DisplayNames(["en"], { type: "language" });
    const name = names.of(languageCode.replace(/-(.)/g, (_, c) => `-${c.toUpperCase()}`));
    return name || languageCode;
  } catch {
    return languageCode;
  }
}

/**
 * Returns the display label for a language code in the format "Language (code)".
 * Uses the SUPPORTED_LANGUAGES list first; falls back to Intl for unknown codes.
 * e.g. "pt-br" → "Portuguese (pt-br)", "en-us" → "English (en-us)"
 */
export function getLanguageLabel(languageCode: string): string {
  const lang = SUPPORTED_LANGUAGES.find((l) => l.value === languageCode);
  if (lang) return lang.text;
  const name = getLanguageName(languageCode);
  return name !== languageCode ? `${name} (${languageCode})` : languageCode;
}

/**
 * Picks the language code that best matches a file name, from the given list of codes.
 * Tries an exact match first, then falls back to a code with the same base language:
 * `preferredCode` if it has that base language (e.g. the current selection), otherwise the first one.
 * e.g. "es-mx.json" → "es-mx"; if missing, "es-es" (first "es" code).
 * "es_MX.json", "app.es-mx.json" and "es-mx (1).json" also work.
 * Returns null when nothing matches.
 */
export function findLanguageCodeForFileName(fileName: string, languageCodes: string[], preferredCode = ""): string | null {
  const baseName = fileName.toLowerCase().replace(/\.json$/, "");
  const lastSegment = baseName.split(".").pop()?.replace(/_/g, "-") ?? "";
  // only keep a leading language code, ignoring suffixes like " (1)" or "-copy"
  const candidate = lastSegment.trim().match(/^[a-z]{2,3}(?:-[a-z0-9]{2,4})?(?![a-z0-9])/)?.[0];
  if (!candidate) return null;

  const exactMatch = languageCodes.find((code) => code.toLowerCase() === candidate);
  if (exactMatch) return exactMatch;

  const baseLanguage = candidate.split("-")[0];
  const hasSameBaseLanguage = (code: string) => code.toLowerCase().split("-")[0] === baseLanguage;
  if (preferredCode && languageCodes.includes(preferredCode) && hasSameBaseLanguage(preferredCode)) return preferredCode;
  return languageCodes.find(hasSameBaseLanguage) ?? null;
}
